import {
  AddOutlined,
  AutoAwesome,
  FilterListRounded,
  SearchRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Card,
  CardHeader,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Chip as FilterChip } from "src/components/Chip/Chip";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { getQualityEventStatistics } from "src/components/QualityEvents/quality-event-statistics";
import { QualityEventFieldValue } from "src/components/QualityEvents/QualityEventFieldValue";
import { useOpenQualityEventAgent } from "src/components/QualityEvents/useOpenQualityEventAgent";
import { QualityEventsSkeleton } from "src/components/Skeletons/QualityEventsSkeleton";
import { AiGradientButton } from "src/components/ui/AiGradientButton";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { GenericFilterMenu } from "src/components/ui/GenericFilterMenu";
import { StatCard } from "src/components/ui/StatCard";
import { useGetUsersByOrg } from "src/hooks/queries";
import { useCreateQualityEventRecord } from "src/hooks/queries/quality-event-mutations";
import {
  useGetQualityEventDefinitions,
  useGetQualityEventRecords,
} from "src/hooks/queries/quality-events";
import { QualityEventValue } from "src/types/quality-events";
import { getUserDisplayName } from "src/utils/user";

const qualityEventRecordStatus = {
  OPEN: { label: "Open", color: "default" },
  IN_REVIEW: { label: "In review", color: "warning" },
  CLOSED: { label: "Closed", color: "success" },
} as const;
export const QualityEvents = () => {
  const { orgId = "", qualityEventKey: requestedQualityEventKey } = useParams();
  const navigate = useNavigate();
  const openAgent = useOpenQualityEventAgent();
  const definitions = useGetQualityEventDefinitions({ orgId });
  const qualityEventKey =
    requestedQualityEventKey ?? definitions.data?.[0]?.key ?? "";
  const records = useGetQualityEventRecords({ orgId, qualityEventKey });
  const users = useGetUsersByOrg({ orgId });
  const create = useCreateQualityEventRecord({ orgId });
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  useEffect(() => setStage(""), [qualityEventKey]);
  const definition = definitions.data?.find(
    (qualityEvent) => qualityEvent.key === qualityEventKey
  );
  if (definitions.isLoading || records.isLoading || users.isLoading)
    return <QualityEventsSkeleton />;
  if (!requestedQualityEventKey && definitions.data?.length === 0)
    return <Alert severity="info">No quality event types are available.</Alert>;
  if (!definition || !records.data || !users.data || users.isError)
    return (
      <Alert severity="error">
        Unable to load quality events. Please refresh and try again.
      </Alert>
    );
  const summaryFields = definition.definition.steps.flatMap((step) =>
    step.fields
      .filter((field) => field.showInOverview)
      .map((field) => ({ ...field, stepKey: step.key }))
  );
  const filteredRecords = records.data.filter(
    (record) =>
      record.title.toLowerCase().includes(search.toLowerCase()) &&
      (!stage ||
        (stage === "closed"
          ? record.status === "CLOSED"
          : record.currentStepKey === stage && record.status !== "CLOSED"))
  );
  const statistics = getQualityEventStatistics(records.data);
  const orgUsers = users.data;
  const statisticCards = [
    [`Open ${definition.name}s`, statistics.open, "primary"],
    ["In review", statistics.inReview, "warning"],
    ["Overdue", statistics.overdue, "error"],
    ["Closed", statistics.closed, "success"],
  ] as const;
  const tableHeaders = [
    "Records",
    "Stage",
    "Status",
    ...summaryFields.map((field) => field.label),
    "Owner",
    "Opened",
  ];
  const qualityEventId = qualityEventKey.toUpperCase();
  const emptyMessage = records.data.length
    ? "No records match these filters."
    : "No records yet.";
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              { label: "Quality Event Management" },
              {
                label: (
                  <CompactSelect
                    textSize="medium"
                    value={qualityEventKey}
                    onChange={(value) =>
                      navigate(`/${orgId}/quality-events/${value}`)
                    }
                    ariaLabel="Quality event type"
                    renderValue={() => definition.name}
                  >
                    {definitions.data?.map((qualityEvent) => (
                      <MenuItem key={qualityEvent.key} value={qualityEvent.key}>
                        {qualityEvent.name}
                      </MenuItem>
                    ))}
                  </CompactSelect>
                ),
              },
              { label: "Overview" },
            ]}
          />
        }
        rightButtons={
          <AiGradientButton
            startIcon={<AutoAwesome />}
            aria-label={`New ${definition.name} with AI`}
            onClick={() => openAgent(definition)}
          >
            New {definition.name}
          </AiGradientButton>
        }
      />
      <div className="flex flex-col gap-4 px-3 py-2">
        <section
          aria-label={`${definition.name} statistics`}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          {statisticCards.map(([label, value, barColor], index) => (
            <StatCard
              key={label}
              label={label}
              value={`${value}`}
              barColor={barColor}
              secondary={index === 0 ? ` / ${records.data.length}` : undefined}
              barValue={
                records.data.length ? (value / records.data.length) * 100 : 0
              }
            />
          ))}
        </section>
        <Card
          component="section"
          variant="outlined"
          className="overflow-hidden"
          aria-label="Quality event register"
        >
          <CardHeader
            title={`${definition.name} records`}
            subheader={definition.description}
            action={
              <LoadingButton
                variant="outlined"
                size="small"
                startIcon={<AddOutlined />}
                loading={create.isPending}
                disabled={create.isPending}
                sx={{
                  flexShrink: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: "text.secondary",
                    backgroundColor: "action.hover",
                  },
                }}
                onClick={() => create.mutate(definition.key)}
              >
                New {definition.name}
              </LoadingButton>
            }
            titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
            subheaderTypographyProps={{
              variant: "body1",
              color: "text.primary",
            }}
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              gap: 2,
              flexWrap: "wrap",
              "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{
              height: 42,
              pl: "10px",
              pr: "7px",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <TextField
              fullWidth
              hiddenLabel
              inputProps={{ "aria-label": "Search records" }}
              placeholder="Search"
              size="small"
              variant="standard"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded
                      aria-hidden="true"
                      sx={{ color: "rgba(100, 116, 139, 0.9)", fontSize: 16 }}
                    />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: 14,
                  "& input": { height: 28, lineHeight: "28px", py: 0 },
                },
              }}
            />
            <GenericFilterMenu
              id="quality-events-filter-menu"
              triggerId="quality-events-filter-button"
              triggerAriaLabel="Filter records"
              closeAriaLabel="Close record filters"
              triggerTooltip="Filter records"
              triggerIcon={<FilterListRounded fontSize="small" />}
              hasActiveFilters={stage !== ""}
              contentTestId="quality-events-filter-content"
              headerTestId="quality-events-filter-header"
              bodyTestId="quality-events-filter-body"
              popperTestId="quality-events-filter-popper"
              placement="bottom-end"
              fallbackPlacements={["bottom-start", "top-end"]}
              onResetFilters={() => setStage("")}
            >
              <Box sx={{ pb: 0.5 }}>
                <Box sx={{ margin: "4px 14px 0", padding: "6px 8px 5px" }}>
                  <Typography fontSize="h6">Stage</Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mx: 1.5,
                    my: 1,
                    px: 1,
                  }}
                >
                  {[
                    ...definition.definition.steps,
                    { key: "closed", title: "Closed" },
                  ].map((step) => {
                    const selected = stage === step.key;
                    return (
                      <FilterChip
                        key={step.key}
                        label={step.title}
                        size="small"
                        aria-pressed={selected}
                        color={selected ? "info" : "default"}
                        onClick={() => setStage(selected ? "" : step.key)}
                        sx={{
                          width: "fit-content",
                          ...(selected
                            ? {
                                "&&, &&:hover": {
                                  backgroundColor: "#5d85ff2b",
                                  color: "inherit",
                                },
                              }
                            : {}),
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </GenericFilterMenu>
          </Stack>
          <TableContainer>
            <Table
              size="small"
              aria-label={`${definition.name} records`}
              sx={{
                border: 0,
                "& .MuiTableCell-root": {
                  px: 2,
                  py: "9px",
                  border: 0,
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "& .MuiTableCell-head": {
                  height: 44,
                  py: 0,
                  backgroundColor: "background.paper",
                },
                "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
                  {
                    borderBottom: 0,
                  },
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((label) => (
                    <TableCell key={label}>
                      <Typography
                        variant="caption"
                        color="text.primary"
                        sx={{ fontWeight: 600, lineHeight: 1.25 }}
                      >
                        {label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((record) => {
                  const href = `/${orgId}/quality-events/${qualityEventKey}/${record.id}`;
                  const status = qualityEventRecordStatus[record.status];
                  return (
                    <TableRow
                      key={record.id}
                      hover
                      className="cursor-pointer"
                      onClick={(event) => {
                        if (
                          event.defaultPrevented ||
                          (event.target instanceof Element &&
                            event.target.closest("a, button"))
                        )
                          return;
                        navigate(href);
                      }}
                    >
                      <TableCell>
                        <Link
                          className="font-semibold text-inherit no-underline hover:underline"
                          to={href}
                        >
                          {record.title || `Untitled ${record.definition.name}`}
                        </Link>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {`${qualityEventId}-${record.id.slice(0, 8).toUpperCase()}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {record.status === "CLOSED"
                          ? "Closed"
                          : definition.definition.steps.find(
                              (step) => step.key === record.currentStepKey
                            )?.title}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="tiny"
                          className="text-sm"
                          color={status.color}
                          label={status.label}
                        />
                      </TableCell>
                      {summaryFields.map((field) => (
                        <TableCell
                          key={`${field.stepKey}:${field.key}`}
                          sx={{
                            "& .MuiTypography-root": { fontSize: "inherit" },
                          }}
                        >
                          <QualityEventFieldValue
                            field={field}
                            value={
                              record.steps[field.stepKey]?.answers[
                                field.key
                              ] as QualityEventValue | undefined
                            }
                            files={[]}
                            orgId={orgId}
                            users={orgUsers}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        {getUserDisplayName(record.createdBy)}
                      </TableCell>
                      <TableCell>
                        {new Date(record.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredRecords.length === 0 && (
            <Typography className="p-6 text-center" color="text.secondary">
              {emptyMessage}
            </Typography>
          )}
        </Card>
      </div>
    </div>
  );
};
